import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';
import { notification } from 'antd';
import moment from "moment";

import { Layout } from '~/components/UI';
import { LoadingTopBar } from '~/components/UI/LoadingTopBar';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import DrawerLeads from "./components/Drawer";
import * as drawerLeadsActions from "./components/Drawer/actions";
import * as leadsActions from "./actions";
import { LeadCalendarDrawer } from "~/containers/LeadCalendarDrawer";
import { Header } from "./components/Header";
import { LeadMetricsModal } from "./components/LeadMetricsModal";
import { ConfettiCanvas } from "./components/ConfettiCanvas";
import { DisplayModeRenderer } from "./components/DisplayModeRenderer";
import { LeadsProvider } from "./context/LeadsContext";
import { useLeadsFiltering } from './hooks/useLeadsFiltering';
import { ProposalsDrawer } from "./components/ProposalsDrawer";
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  LEADS_FILTERS_KEY,
  LEADS_VIEW_KEY,
  getDefaultLeadsFilters,
  getDefaultLeadsView,
  normalizeLeadsDisplayMode,
  normalizeLeadsMonth,
  getLeadsMonthRequest,
  shouldUseLeadsCache,
  getLeadsTablePeriodRequest,
  normalizeLeadsPeriod,
  countActiveLeadsFilters,
  getLeadsFilterPills,
  getLeadsRestoredSummary,
  getLeadsFilterReset,
} from './helpers/filters';

moment.updateLocale("pt-br", {
  weekdaysMin: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
});

var EMPTY_USERS = [];

const Leads = ({
  columns,
  showDrawer,
  saveMetrics,
  currentMetrics,
  currentSummary,
  moveLead,
  isLoading,
  isLeadsDrawerVisible,
  finishCurrentOnboardingStep,
  user,
  users,
  hideLeads,
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [isLoadingData, setIsLoadingData] = useState(true);

  // Filters kept across refreshes for a few minutes (per tenant/user/page):
  // month (funnel/calendar), search, user, table period. See helpers/filters.js.
  var persisted = usePersistedFilters(LEADS_FILTERS_KEY, getDefaultLeadsFilters);
  var filters = persisted.filters;
  var setFilters = persisted.setFilters;
  var defaults = persisted.defaults;
  var resetFilters = persisted.resetFilters;

  // View choice (table, calendar, funnel): kept too, but not a filter.
  var persistedView = usePersistedFilters(LEADS_VIEW_KEY, getDefaultLeadsView);
  var setView = persistedView.setFilters;
  var displayMode = normalizeLeadsDisplayMode(persistedView.filters.displayMode);
  var setDisplayMode = useCallback(function(mode) {
    setView({ displayMode: mode });
  }, [setView]);

  var date = normalizeLeadsMonth(filters.month, defaults.month); // 'MM-YYYY'
  var setDate = useCallback(function(month) {
    setFilters({ month: month });
  }, [setFilters]);

  var searchQuery = filters.search || '';
  var setSearchQuery = useCallback(function(value) {
    setFilters({ search: value });
  }, [setFilters]);

  var selectedUserId = filters.userId !== undefined ? filters.userId : null;
  var setSelectedUserId = useCallback(function(userId) {
    setFilters({ userId: userId !== undefined ? userId : null });
  }, [setFilters]);

  // Use the new filtering hook that combines search and user filtering
  const {
    filteredColumns,
    filteredHideLeads,
    searchResultsCount,
  } = useLeadsFiltering(columns, hideLeads, searchQuery, selectedUserId);
  const [isMetricsModalVisible, setIsMetricsModalVisible] = useState(false);
  const [isShowingConffeti, setIsShowingConffeti] = useState(false);
  const [funnelData, setFunnelData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [updatedSummaryCards, setUpdatedSummaryCards] = useState(new Set());
  const [calendarDrawer, setCalendarDrawer] = useState({
    isOpen: false,
    followUps: [],
    meetings: [],
  });
  const [proposalsDrawer, setProposalsDrawer] = useState({
    isOpen: false,
    month: null,
    year: null,
  });

  // table mode: applied period ({ from, to, filterBy }) for LeadsTable. A new
  // object on every "Filtrar" so submitting the same range still reloads.
  const [periodSubmitCount, setPeriodSubmitCount] = useState(0);
  var period = useMemo(function() {
    return getLeadsTablePeriodRequest(filters);
  }, [filters.periodFrom, filters.periodTo, filters.filterBy, periodSubmitCount]);
  var handlePeriodSubmit = useCallback(function(value) {
    setFilters(Object.assign({ filterBy: value.filterBy }, normalizeLeadsPeriod(value.from, value.to)));
    setPeriodSubmitCount(function(count) { return count + 1; });
  }, [setFilters]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsKeys, setSelectedRowsKeys] = useState([]);

  const scrollContainerRef = useRef(null);

  // Scroll navigation state
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const loadSummaryData = useCallback(async (highlightChanges = false) => {
    try {
      // Store previous funnel data for comparison if highlighting changes
      const previousFunnelData = highlightChanges ? funnelData : null;
      
      const summaryResult = await dispatch(leadsActions.loadSummary({ showLoading: false }));
      
      if (summaryResult && summaryResult.payload && summaryResult.payload.data) {
        // Extract funnel data and summary data using double data prop
        const summaryFunnelData = (summaryResult.payload.data.data && summaryResult.payload.data.data.funnel) || null;
        const summaryInfo = (summaryFunnelData && summaryFunnelData.summary) || null;
        
        if (summaryFunnelData) {
          setFunnelData(summaryFunnelData);
        }
        
        if (summaryInfo) {
          setSummaryData(summaryInfo);
        }
          
        // Highlight changed cards if requested
        if (highlightChanges && previousFunnelData && previousFunnelData.current_period && summaryFunnelData && summaryFunnelData.current_period) {
            const changedCards = new Set();
            const currentPeriod = summaryFunnelData.current_period;
            const previousPeriod = previousFunnelData.current_period;
            
            // Check each funnel metric for changes
            ['cliques', 'leads', 'oportunidades', 'propostas', 'positivo', 'negativo'].forEach(metric => {
              const currentValue = currentPeriod[metric] || 0;
              const previousValue = previousPeriod[metric] || 0;
              
              if (currentValue !== previousValue) {
                changedCards.add(metric);
              }
            });
            
            if (changedCards.size > 0) {
              setUpdatedSummaryCards(changedCards);
              
              // Show a subtle notification about the update
              if (typeof notification !== 'undefined') {
                notification.success({
                  message: 'Resumo Atualizado',
                  description: `${changedCards.size} métrica${changedCards.size > 1 ? 's' : ''} atualizada${changedCards.size > 1 ? 's' : ''}`,
                  duration: 2,
                  placement: 'bottomRight',
                });
              }
              
              // Remove highlight after 3 seconds
              setTimeout(() => {
                setUpdatedSummaryCards(new Set());
              }, 3000);
            }
        }
        
        // Cache summary data
        await LocalCacheHandler.save({
          pathname: pathname + '_summary',
          data: {
            summary: summaryInfo,
            funnel: summaryFunnelData,
          },
        });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading summary data:', error);
      return false;
    }
  }, [dispatch, pathname, funnelData]);

  const loadData = useCallback(async () => {
    if (!isMountedRef.current) return;

    setIsLoadingData(true);

    // The cache key has no month, so it only holds the current month: a
    // restored or navigated month must not show another month's columns.
    const useCache = shouldUseLeadsCache(date, defaults.month);
    
    try {
      if (useCache) {
        await LocalCacheHandler.load({
          actionName: CACHE_ACTIONS.leads,
          dispatch,
          pathname,
        });
      }
      
      if (!isMountedRef.current) return;
      
      // Month and year of the applied (possibly restored) month
      const monthRequest = getLeadsMonthRequest(date);
      const month = monthRequest.month;
      const year = monthRequest.year;
      
      const leadsResult = await dispatch(leadsActions.loadLeads({ 
        month, 
        year, 
        showLoading: false 
      }));
      
      if (!isMountedRef.current) return;

      // Check if the results are successful - flexible success check
      if (!leadsResult || !leadsResult.payload || !leadsResult.payload.data) {
        throw new Error('Failed to load leads data');
      }
      
      // Extract funnel data using double data prop structure
      const apiResponseFunnelData = (leadsResult.payload.data.data && leadsResult.payload.data.data.funnel) || null;
      
      // Extract summary data with revenue information from funnel.summary
      const apiResponseSummaryData = (apiResponseFunnelData && apiResponseFunnelData.summary) || null;
      
      if (apiResponseFunnelData) {
        setFunnelData(apiResponseFunnelData);
      } else {
        setFunnelData(null);
      }
      
      if (apiResponseSummaryData) {
        setSummaryData(apiResponseSummaryData);
      } else {
        setSummaryData(null);
      }
      
      if (useCache) await LocalCacheHandler.save({
        pathname,
        data: {
          columns: (leadsResult.payload.data.data && leadsResult.payload.data.data.stages) || [], // Save stages data using double data prop
          hideLeads: (leadsResult.payload.data.data && leadsResult.payload.data.data.hideLeads) || false,
          funnel: apiResponseFunnelData, // Save funnel data to cache
          summary: apiResponseSummaryData, // Save summary data with revenue
          meta: leadsResult.payload.meta, // Save stages config and other meta
        },
      });
    } catch (error) {
      console.error('Error loading leads data:', error);
      notification.error({
        message: 'Erro ao carregar dados',
        description: 'Ocorreu um erro ao carregar os dados dos leads. Tente novamente.',
        duration: 4.5,
      });
    } finally {
      if (isMountedRef.current) {
        setIsLoadingData(false);
      }
    }
  }, [dispatch, pathname, date, defaults.month]);

  // Loads the applied month: on mount (restored or current) and on change.
  useEffect(() => {
    loadData();
  }, [loadData]);


  const startShowingConfetti = useCallback(() => {
    setIsShowingConffeti(true);
  }, []);

  const stopShowingConfetti = useCallback(() => {
    setIsShowingConffeti(false);
  }, []);

  const closeCalendarDrawer = useCallback(() => {
    setCalendarDrawer({
      isOpen: false,
      followUps: [],
      meetings: [],
    });
  }, []);

  const openCalendarDrawer = useCallback(({ followUps, meetings }) => {
    setCalendarDrawer({
      isOpen: true,
      followUps,
      meetings,
    });
  }, []);

  const closeMetricsModal = useCallback(() => {
    setIsMetricsModalVisible(false);
  }, []);

  const handleSummaryCardClick = useCallback((summaryData) => {
    // Handle Propostas card click - open drawer with proposals list
    if (summaryData && summaryData.stage === 'propostas') {
      var monthRequest = getLeadsMonthRequest(date);
      setProposalsDrawer({
        isOpen: true,
        month: monthRequest.month,
        year: monthRequest.year,
      });
    }
  }, [date]);

  var closeProposalsDrawer = useCallback(function() {
    setProposalsDrawer({
      isOpen: false,
      month: null,
      year: null,
    });
  }, []);


  // Navigation scroll functions
  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -280, behavior: 'smooth' });
      // Check scroll state after a short delay to allow smooth scroll to complete
      setTimeout(() => {
        checkScrollButtons();
      }, 400);
    }
  }, [checkScrollButtons]);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 280, behavior: 'smooth' });
      // Check scroll state after a short delay to allow smooth scroll to complete
      setTimeout(() => {
        checkScrollButtons();
      }, 400);
    }
  }, [checkScrollButtons]);

  // Check scroll buttons state
  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const canLeft = scrollLeft > 0;
      const canRight = scrollLeft < scrollWidth - clientWidth - 1;
      
      setCanScrollLeft(canLeft);
      setCanScrollRight(canRight);
    }
  }, []);

  // Listen for scroll events and column changes to update navigation buttons
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons(); // Initial check
      
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
      };
    }
  }, [checkScrollButtons]);

  // Update scroll buttons when columns change
  useEffect(() => {
    if (scrollContainerRef.current) {
      // Use timeout to ensure DOM is updated
      setTimeout(checkScrollButtons, 100);
    }
  }, [checkScrollButtons, columns]);

  // Auto-refresh summary when leads are modified
  const previousColumnsRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    // Skip initial render
    if (!previousColumnsRef.current) {
      previousColumnsRef.current = JSON.stringify(columns);
      return;
    }

    const currentColumnsStr = JSON.stringify(columns);
    
    // Check if columns actually changed and we have data
    if (previousColumnsRef.current !== currentColumnsStr && columns.length > 0) {
      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Debounce the refresh to avoid too many calls
      refreshTimeoutRef.current = setTimeout(() => {
        loadSummaryData(true); // true = highlight changes
      }, 1200); // Wait a bit longer to ensure backend is updated
    }
    
    previousColumnsRef.current = currentColumnsStr;

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [columns, loadSummaryData]);

  // Pills, badge and restored notice reflect the applied filters.
  var pills = getLeadsFilterPills(filters, defaults, displayMode, { users: users })
    .map(function(pill) {
      return {
        key: pill.key,
        label: pill.label,
        color: pill.color,
        icon: pill.icon,
        onRemove: pill.removable ? function() { setFilters(getLeadsFilterReset(pill.key, defaults)); } : undefined,
      };
    });
  var filterCount = countActiveLeadsFilters(filters, defaults, displayMode);
  var restoredSummary = getLeadsRestoredSummary(filters, defaults, { users: users });
  var handleClearFilters = resetFilters;

  // Create context value with shared state and handlers
  const leadsContextValue = {
    // Data
    columns: filteredColumns,
    originalColumns: columns, // Keep original for reference
    hideLeads: filteredHideLeads,
    currentSummary,
    funnelData, // Add funnel data to context
    summaryData, // Add summary data with revenue to context
    updatedSummaryCards, // Add updated cards tracking
    date,
    period,
    displayMode,
    user,
    isLoading,
    selectedRows,
    selectedRowsKeys,
    searchQuery,
    searchResultsCount,
    
    // Handlers
    showDrawer,
    moveLead,
    startShowingConfetti,
    setDate,
    openCalendarDrawer,
    setSelectedRows,
    setSelectedRowsKeys,
    setSearchQuery,
    handleSummaryCardClick,
    loadSummaryData,
    
    // Scroll handlers
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight
  };

  return (
    <LeadsProvider value={leadsContextValue}>
      <div>
      <LoadingTopBar isActive={isLoadingData} />
      <Layout>
        <ConfettiCanvas 
          isShowingConfetti={isShowingConffeti} 
          onStop={stopShowingConfetti} 
        />
        <Header
          date={date}
          setDate={setDate}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          setIsMetricsModalVisible={setIsMetricsModalVisible}
          showDrawer={showDrawer}
          isLeadsDrawerVisible={isLeadsDrawerVisible}
          finishCurrentOnboardingStep={finishCurrentOnboardingStep}
          user={user}
          selectedRowsKeys={selectedRowsKeys}
          selectedRows={selectedRows}
          columns={filteredColumns}
          period={period}
          onPeriodSubmit={handlePeriodSubmit}
          isLoading={isLoading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedUserId={selectedUserId}
          onUserChange={setSelectedUserId}
          pills={pills}
          filterCount={filterCount}
          onClearAll={persisted.isDefault ? undefined : handleClearFilters}
        />
        <RestoredFiltersNotice
          visible={persisted.wasRestored}
          summary={restoredSummary}
          onReset={handleClearFilters}
          onDismiss={persisted.dismissRestored}
        />
        <DisplayModeRenderer 
          displayMode={displayMode}
          showDrawer={() => {}} // Empty function for now
        />
        <LeadMetricsModal
          isVisible={isMetricsModalVisible}
          closeModal={closeMetricsModal}
          selectedDate={date}
          onSaveMetrics={saveMetrics}
          currentMetrics={currentMetrics}
        />
        <DrawerLeads />
        <LeadCalendarDrawer
          isVisible={calendarDrawer.isOpen}
          onClose={closeCalendarDrawer}
          followUps={calendarDrawer.followUps}
          meetings={calendarDrawer.meetings}
          onOpenDrawerLeads={showDrawer}
        />
        <ProposalsDrawer
          isVisible={proposalsDrawer.isOpen}
          onClose={closeProposalsDrawer}
          showDrawer={showDrawer}
          month={proposalsDrawer.month}
          year={proposalsDrawer.year}
        />
      </Layout>
      </div>
    </LeadsProvider>
  );
};

const mapStateToProps = (state) => ({
  columns: (state.leads && state.leads.columns) || [],
  hideLeads: (state.leads && state.leads.hideLeads) || false,
  currentMetrics: (state.leads && state.leads.metrics && state.leads.metrics.currentMetrics) || null,
  currentSummary: (state.leads && state.leads.metrics && state.leads.metrics.currentSummary) || {},
  isLoading: state.leads && state.leads.isLoading !== undefined ? state.leads.isLoading : true,
  isLeadsDrawerVisible: (state.leads && state.leads.drawer && state.leads.drawer.show) || false,
  user: state.user,
  users: state.users || EMPTY_USERS,
});

const mapDispatchProps = (dispatch) => ({
  showDrawer: (data = null) => dispatch(drawerLeadsActions.show(data)),
  moveLead: (...props) => dispatch(leadsActions.move(props)),
  saveMetrics: (data) => dispatch(leadsActions.saveMetrics(data)),
});

export default connect(mapStateToProps, mapDispatchProps)(Leads);
